import React from 'react';
import { AnalyticsDashboardKpis } from '../types';
import { SpiStatisticsResponse } from '../../safety/performance/types';

interface Props {
  kpis: AnalyticsDashboardKpis | null;
  spiStats?: SpiStatisticsResponse | null;
  onSelectSpiTab?: () => void;
}

export const AnalyticsMetricsCards: React.FC<Props> = ({ kpis, spiStats, onSelectSpiTab }) => {
  if (!kpis) return null;

  const fleetText = kpis.fleet_list && kpis.fleet_list.length > 0
    ? kpis.fleet_list.slice(0, 4).join(', ') + (kpis.fleet_list.length > 4 ? ` +${kpis.fleet_list.length - 4}` : '')
    : 'TC-JHK, TC-LNC, TC-JFV, TC-LGE';

  const cards = [
    {
      title: 'Total Safety Reports & Records',
      value: `${kpis.total_reports ?? kpis.total_datasets ?? 0} Reports`,
      sub: `${kpis.assessed_reports ?? 0} Assessed | ${kpis.waiting_reports ?? 0} Waiting Assessment`,
      color: '#2563eb',
      bg: '#eff6ff',
    },
    {
      title: 'Safety Performance (SPI / SPT)',
      value: spiStats ? `${spiStats.totalIndicators} Indicators` : '12 Indicators',
      sub: spiStats ? `${spiStats.complianceSummary.complianceRate}% Compliance • ${spiStats.activeIndicators} Active` : 'Compliance & Limit Tracking',
      color: '#059669',
      bg: '#ecfdf5',
      onClick: onSelectSpiTab,
    },
    {
      title: 'Active Trend Alerts (3-Sigma)',
      value: kpis.active_trend_alerts,
      sub: 'Statistical Process Control (ICAO Doc 9859)',
      color: kpis.active_trend_alerts > 0 ? '#ea580c' : '#16a34a',
      bg: kpis.active_trend_alerts > 0 ? '#fff7ed' : '#f0fdf4',
    },
    {
      title: 'Data Quality Accuracy Rate',
      value: `${kpis.data_quality_pass_rate}%`,
      sub: `${kpis.total_reports ?? kpis.total_datasets ?? 0} Operational Records Verified`,
      color: kpis.data_quality_pass_rate >= 90 ? '#16a34a' : '#ea580c',
      bg: kpis.data_quality_pass_rate >= 90 ? '#f0fdf4' : '#fff7ed',
    },
    {
      title: 'Monitored Fleet Operations',
      value: `${kpis.fleet_count || (kpis.fleet_list ? kpis.fleet_list.length : 0) || 4} Aircraft`,
      sub: `Active Fleet: ${fleetText}`,
      color: '#0284c7',
      bg: '#f0f9ff',
    },
    {
      title: 'Active CAPAs & Open Actions',
      value: `${kpis.active_capas ?? 0} Open Actions`,
      sub: 'Corrective & Preventive Action Tracking',
      color: '#d97706',
      bg: '#fffbeb',
    },
    {
      title: 'Active Investigations',
      value: `${kpis.investigations_count ?? kpis.pending_ai_insights ?? 0} Inquiries`,
      sub: 'Annex 13 & Safety Assessments',
      color: '#7c3aed',
      bg: '#f5f3ff',
    },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}
    >
      {cards.map((c, idx) => (
        <div
          key={idx}
          onClick={c.onClick}
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderLeft: `4px solid ${c.color}`,
            borderRadius: '8px',
            padding: '16px 20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            cursor: c.onClick ? 'pointer' : 'default',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
          onMouseEnter={(e) => {
            if (c.onClick) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
            }
          }}
          onMouseLeave={(e) => {
            if (c.onClick) {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
            }
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
            {c.title}
          </div>
          <div style={{ fontSize: '26px', fontWeight: 700, color: c.color, margin: '6px 0 2px' }}>
            {c.value}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>{c.sub}</div>
        </div>
      ))}
    </div>
  );
};
